import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getProducts } from "../lib/api";
import { IMG, REAL_LIFE, GALLERY, MOODS, ACTIVITIES } from "../lib/assets";
import { ProductCard } from "../components/ProductCard";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";

export default function Home() {
  const [best, setBest] = useState([]);

  useEffect(() => {
    getProducts({ best_seller: true }).then((d) => setBest(d.slice(0, 5)));
  }, []);

  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      {/* HERO */}
      <section className="relative w-full">
        {/* Desktop / tablet group hero with left text overlay */}
        <div className="relative hidden lg:block h-[92vh] min-h-[640px] w-full overflow-hidden bg-[#EDE6DA]">
          <motion.img src={IMG.heroDesktop} alt="Four women wearing SV Athletic Wear"
            initial={{ scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 h-full w-full object-cover object-[72%_center]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#EDE6DA]/95 via-[#EDE6DA]/45 to-transparent" />
          <div className="relative container-luxe h-full flex flex-col justify-center">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="max-w-xl text-charcoal">
              <p className="overline text-gold mb-4">SV Athletic Wear</p>
              <h1 className="h-display text-5xl sm:text-7xl lg:text-8xl">Move With<br />Confidence.</h1>
              <p className="mt-6 text-base sm:text-lg font-light text-charcoal/75 max-w-md">Designed for every woman, every season, and every move.</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/shop" className="btn-primary" data-testid="hero-shop-btn">Shop Collection</Link>
                <Link to="/shop?collection=Matching%20Sets" className="btn-secondary" data-testid="hero-new-btn">Explore Matching Sets</Link>
              </div>
            </motion.div>
          </div>
        </div>
        {/* Mobile: complete group photograph first */}
        <div className="lg:hidden relative w-full aspect-[3/4] overflow-hidden bg-[#EDE6DA]">
          <motion.img src={IMG.heroMobile} alt="Four women wearing SV Athletic Wear"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, ease: "easeOut" }}
            className="absolute inset-0 h-full w-full object-cover object-top" data-testid="hero-mobile-image" />
        </div>
        {/* Mobile content block (below the image) */}
        <div className="lg:hidden bg-[#EDE6DA] text-charcoal" data-testid="hero-mobile-content">
          <div className="container-luxe pt-10 pb-12">
            <p className="overline text-gold mb-3">SV Athletic Wear</p>
            <h1 className="h-display text-5xl sm:text-6xl">Move With<br />Confidence.</h1>
            <p className="mt-5 text-base font-light text-charcoal/75 max-w-md">Designed for every woman, every season, and every move.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/shop" className="btn-primary text-center" data-testid="hero-shop-btn-mobile">Shop Collection</Link>
              <Link to="/shop?collection=Matching%20Sets" className="btn-secondary text-center" data-testid="hero-new-btn-mobile">Explore Matching Sets</Link>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["Wear Confidence", "Performance Driven", "Everyday Essentials", "Designed For Movement", "Sculpted To Last"]} />

      {/* HOW SHE MOVES — activity series */}
      <section className="container-wide py-20" data-testid="how-she-moves">
        <Reveal>
          <div className="mb-10 max-w-xl">
            <p className="overline text-gold mb-3">Made To Move</p>
            <h2 className="h-display text-4xl sm:text-5xl mb-4">How She Moves</h2>
            <p className="text-sm text-muted-foreground font-light">From morning miles to strength days and studio flow—move your way.</p>
          </div>
        </Reveal>
        <div className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory gap-4 lg:gap-4 lg:grid lg:grid-cols-3 lg:overflow-visible -mx-1 px-1 lg:mx-0 lg:px-0">
          {ACTIVITIES.map((a, i) => (
            <Reveal key={a.label} delay={i * 0.08} className="snap-start shrink-0 lg:shrink w-[84vw] sm:w-[62vw] md:w-[46vw] lg:w-auto">
              <Link to={a.to} className="group block" data-testid={`activity-card-${i}`}>
                <div className="relative aspect-[4/5] lg:aspect-auto lg:h-[680px] overflow-hidden bg-[#EDE6DA]">
                  <img src={a.img} alt={`${a.label} — SV Athletic Wear`} loading="lazy"
                    className="h-full w-full object-cover object-top motion-safe:transition-transform motion-safe:duration-700 motion-safe:ease-out motion-safe:group-hover:scale-[1.03]" />
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-0 p-6 lg:p-8 text-bone">
                    <h3 className="font-serif text-3xl lg:text-4xl leading-none">{a.label}</h3>
                    <p className="mt-2 text-sm font-light text-bone/85">{a.sub}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="container-wide py-16" data-testid="best-sellers">
        <Reveal>
          <div className="text-center mb-12">
            <p className="overline text-gold mb-3">The SV Edit</p>
            <h2 className="h-display text-4xl sm:text-5xl">Best Sellers</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-4 gap-y-10">
          {best.map((p, i) => (<Reveal key={p.id} delay={i * 0.08}><ProductCard product={p} /></Reveal>))}
        </div>
      </section>

      {/* WHY SV — split */}
      <section className="py-20" data-testid="why-sv">
        <div className="container-luxe grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal><div className="aspect-[4/5] overflow-hidden bg-beige/40"><img src={IMG.whySv} alt="SV garment detail" className="h-full w-full object-cover" /></div></Reveal>
          <Reveal delay={0.1}>
            <div className="lg:pl-8">
              <p className="overline text-gold mb-4">Why SV Athletic Wear</p>
              <h2 className="h-display text-4xl sm:text-5xl mb-8">Engineered To<br />Empower.</h2>
              <div className="space-y-8">
                {[["SculptKnit™ Fabric", "Four-way stretch compression that sculpts and supports without ever restricting."],
                  ["Made To Last", "Premium, responsibly sourced materials tested for hundreds of wears and washes."],
                  ["Designed For Her", "Every silhouette is fit-tested on real women, ages 18 to 65."]].map(([t, d]) => (
                  <div key={t} className="border-t border-hairline pt-6">
                    <h3 className="font-serif text-2xl mb-2">{t}</h3>
                    <p className="text-sm text-muted-foreground font-light leading-relaxed max-w-md">{d}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONFIDENCE AT EVERY CHAPTER — full-bleed 50/50 editorial */}
      <section className="grid grid-cols-1 lg:grid-cols-2 items-stretch" data-testid="every-chapter">
        <Reveal className="min-h-[60vh] lg:min-h-[80vh]">
          <div className="h-full w-full overflow-hidden bg-[#EDE6DA]">
            <img src={IMG.silverIce} alt="Woman moving with confidence in SV Athletic Wear" className="h-full w-full object-cover object-top" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="flex items-center bg-bone">
          <div className="px-8 sm:px-14 lg:px-20 py-16 lg:py-0 max-w-xl">
            <p className="overline text-gold mb-4">Confidence At Every Chapter</p>
            <h2 className="h-display text-4xl sm:text-5xl lg:text-6xl mb-6">Designed To Move With You.</h2>
            <p className="text-base text-muted-foreground font-light leading-relaxed mb-8">Strength, style, and confidence do not belong to one age. SV is imagined for women moving through every season of life.</p>
            <Link to="/shop" className="btn-secondary" data-testid="every-chapter-link">Explore The Collection</Link>
          </div>
        </Reveal>
      </section>

      {/* MATCH YOUR MOOD — editorial color story */}
      <section className="bg-beige/40 py-20" data-testid="match-your-mood">
        <div className="container-wide">
          <Reveal>
            <div className="mb-10 max-w-xl">
              <p className="overline text-gold mb-3">Shop The Look</p>
              <h2 className="h-display text-4xl sm:text-5xl mb-4">Match Your Mood</h2>
              <p className="text-sm text-muted-foreground font-light">Five ways to move. Choose the energy, wear the confidence.</p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 auto-rows-[220px] sm:auto-rows-[260px] lg:auto-rows-[330px] gap-3">
            {MOODS.map((m, i) => (
              <Reveal key={m.color} delay={i * 0.05} className={i === 0 ? "col-span-2 row-span-2" : ""}>
                <Link to={m.to} className="group block h-full" data-testid={`mood-card-${i}`}>
                  <div className="relative h-full min-h-[240px] overflow-hidden bg-[#EDE6DA]">
                    <img src={m.img} alt={`${m.mood} — ${m.color}`} loading="lazy"
                      className="h-full w-full object-cover object-top motion-safe:transition-transform motion-safe:duration-700 motion-safe:group-hover:scale-[1.03]" />
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-0 p-5 lg:p-6 text-bone">
                      <p className="overline text-[10px] text-bone/80 mb-1">{m.color}</p>
                      <h3 className={`font-serif leading-none ${i === 0 ? "text-3xl lg:text-5xl" : "text-2xl lg:text-3xl"}`}>{m.mood}</h3>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SV OFF DUTY */}
      <section className="py-20" data-testid="lifestyle-gallery">
        <div className="container-wide">
          <Reveal><div className="mb-10"><h2 className="h-display text-4xl sm:text-5xl">SV Off Duty</h2><p className="mt-4 text-base sm:text-lg font-light text-muted-foreground max-w-xl">Made for the workout—and everything that comes after.</p></div></Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 px-2">
          {GALLERY.map((src, i) => (
            <div key={i} className="group relative aspect-[3/4] overflow-hidden bg-beige/40" data-testid={`gallery-${i}`}>
              <img src={src} alt="SV Off Duty" loading="lazy" className="h-full w-full object-cover img-zoom" />
              <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-colors" />
            </div>
          ))}
        </div>
      </section>

      {/* DESIGNED FOR REAL LIFE */}
      <section className="bg-charcoal text-bone py-20" data-testid="real-life-section">
        <div className="container-luxe text-center max-w-3xl mx-auto">
          <Reveal>
            <p className="overline text-gold mb-6">SV Athletic Wear</p>
            <h2 className="h-display text-4xl sm:text-5xl mb-8">{REAL_LIFE.heading}</h2>
            <p className="font-serif text-xl sm:text-2xl font-light leading-relaxed text-bone/85">{REAL_LIFE.copy}</p>
          </Reveal>
        </div>
      </section>

      {/* NEWSLETTER */}
      <NewsletterBlock />
    </div>
  );
}

function NewsletterBlock() {
  const [email, setEmail] = useState("");
  return (
    <section className="py-20 relative overflow-hidden" data-testid="newsletter-section">
      <div className="container-luxe text-center max-w-2xl mx-auto">
        <Reveal>
          <p className="overline text-gold mb-4">The Movement Begins Soon</p>
          <h2 className="h-display text-4xl sm:text-6xl mb-6">Confidence, Delivered.</h2>
          <p className="text-sm text-muted-foreground font-light mb-10">Our newsletter arrives with the full SV collection. Sign-up opens soon.</p>
          <form onSubmit={(e) => e.preventDefault()} className="flex max-w-md mx-auto border-b border-charcoal" data-testid="home-newsletter-form">
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" disabled placeholder="Email address"
              data-testid="home-newsletter-input" className="flex-1 bg-transparent py-4 outline-none text-sm placeholder:text-muted-foreground disabled:opacity-60 disabled:cursor-not-allowed" />
            <button type="submit" disabled data-testid="home-newsletter-submit" className="overline text-[11px] px-4 disabled:opacity-70 disabled:cursor-not-allowed">Coming Soon</button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
